import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check, Pencil, Trash2 } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Carregando, Erro } from '../../componentes/estados';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { del, get, mensagemDeErro, post } from '../../nucleo/api';
import { data, dinheiro, nomeCadastro, nomeParceiro, numero, textoLivre } from '../../nucleo/formato';
import { useSessaoFluxo } from './AvisoModo';
import { Linha, Secao } from './comum';
import { FormCotacao } from './FormCotacao';
import { invalidarCompras, qtd, type CotacaoDetalhe, type RequisicaoDetalhe } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';
import { anunciarOcultos } from '../../componentes/sensivel';
import { LinkParceiro } from '../../componentes/link-parceiro';

/** Ficha da cotação (M10.1): itens com preço e prazo, escolha como vencedora, edição e exclusão. */
export function DetalheCotacao({ id, aoFechar }: { id: string | null; aoFechar: () => void }) {
  const { t } = useTranslation('compras');
  const { t: tn } = useTranslation();
  // Fase 2 (W8): no modo simplificado (ou sem Estoque, no recebimento) as ações do fluxo detalhado somem; a leitura fica.
  const s = useSessaoFluxo();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['/compras/cotacoes', 'obter', id], queryFn: () => get<CotacaoDetalhe>(`/compras/cotacoes/${id}`), enabled: !!id });
  const d = q.data;
  const requisicao = useQuery({ queryKey: ['/compras/requisicoes', 'obter', d?.requisicao_id], queryFn: () => get<RequisicaoDetalhe>(`/compras/requisicoes/${d!.requisicao_id}`), enabled: !!d });
  const [editando, setEditando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);

  const escolher = useMutation({
    mutationFn: () => post(`/compras/cotacoes/${id}/escolher`),
    onSuccess: async () => {
      await invalidarCompras(qc);
      toast.sucesso(t('mensagens.cotacaoEscolhida'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const excluir = useMutation({
    mutationFn: () => del(`/compras/cotacoes/${id}`),
    onSuccess: async () => {
      await invalidarCompras(qc);
      setExcluindo(false);
      toast.sucesso(tn('comum.removido'));
      aoFechar();
    },
    onError: (e) => {
      setExcluindo(false);
      toast.erro(mensagemDeErro(e));
    },
  });

  const emAnalise = requisicao.data?.estado === 'enviada' || requisicao.data?.estado === 'aprovada';
  const podeEscolher = !!d && !d.escolhida && !d.vencida && requisicao.data?.estado === 'aprovada' && s.pode('compras.aprovar');
  const podeMexer = !!d && !d.escolhida && emAnalise;

  return (
    <>
      <Drawer
        aberto={!!id}
        aoFechar={aoFechar}
        largura="lg"
        titulo={d ? t('cotacao.titulo', { fornecedor: nomeParceiro(d.fornecedor_nome) }) : t('cotacao.tituloCurto')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={aoFechar}>
              {tn('comum.fechar')}
            </Botao>
            {d && podeMexer && s.pode('compras.excluir') && (
              <Botao variante="perigo" icone={<Trash2 className="size-4" />} onClick={() => setExcluindo(true)}>
                {tn('comum.excluir')}
              </Botao>
            )}
            {d && podeMexer && s.pode('compras.editar') && (
              <Botao icone={<Pencil className="size-4" />} onClick={() => setEditando(true)}>
                {tn('comum.editar')}
              </Botao>
            )}
            {d && podeEscolher && (
              <Botao variante="primario" icone={<Check className="size-4" />} carregando={escolher.isPending} onClick={() => escolher.mutate()}>
                {t('acoes.escolherCotacao')}
              </Botao>
            )}
          </>
        }
      >
        {q.isPending && !!id && <Carregando />}
        {q.isError && <Erro erro={q.error} aoTentar={() => q.refetch()} />}
        {d && (
          <>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              {d.escolhida && <Selo cor="verde">{t('cotacao.escolhida')}</Selo>}
              {d.vencida && <Selo cor="laranja">{t('cotacao.vencida')}</Selo>}
            </div>
            <p className="numero mb-1 text-3xl font-semibold text-tinta-1">{anunciarOcultos(dinheiro(d.total_centavos, d.moeda))}</p>
            <p className="mb-4 text-sm text-tinta-3">{t('cotacao.totalDaCotacao')}</p>
            <dl className="divide-y divide-borda">
              <Linha rotulo={t('campos.requisicao')}>{d.requisicao_numero}</Linha>
              <Linha rotulo={t('campos.fornecedor')}>
                <LinkParceiro id={d.fornecedor_id} nome={d.fornecedor_nome} />
              </Linha>
              <Linha rotulo={t('campos.validade')}>
                {d.validade ? <span className={d.vencida ? 'font-medium text-laranja' : undefined}>{data(d.validade)}</span> : ''}
              </Linha>
              <Linha rotulo={t('campos.condicao')}>{d.condicao_pagamento === 'avista' ? t('pedido.avista') : (d.condicao_pagamento ?? '')}</Linha>
              <Linha rotulo={t('campos.prazoMaximo')}>{d.prazo_maximo_dias === null ? '' : t('comparativo.prazoDias', { count: d.prazo_maximo_dias })}</Linha>
              {d.observacao && <Linha rotulo={t('campos.observacao')}>{textoLivre(d.observacao)}</Linha>}
            </dl>
            <Secao titulo={t('secoes.itens')}>
              <ul className="divide-y divide-borda rounded-lg border border-borda">
                {d.itens_detalhe.map((i) => (
                  <li key={i.produto_id} className="flex min-w-0 items-start justify-between gap-3 px-3 py-2 text-sm">
                    <span className="min-w-0">
                      <span className="block truncate" title={i.produto_nome ?? undefined}>{nomeCadastro(i.produto_nome)}</span>
                      <TextoCortado className="block text-xs text-tinta-3">
                        {i.produto_codigo} · {qtd(i.quantidade, numero)} {i.unidade ?? ''} x {anunciarOcultos(dinheiro(i.preco_unitario_centavos, d.moeda))}
                        {i.prazo_dias !== null ? ` · ${t('comparativo.prazoDias', { count: i.prazo_dias })}` : ''}
                      </TextoCortado>
                    </span>
                    <span className="numero shrink-0">{anunciarOcultos(dinheiro(i.total_centavos, d.moeda))}</span>
                  </li>
                ))}
              </ul>
            </Secao>
          </>
        )}
      </Drawer>
      <FormCotacao aberto={editando && !!d} cotacao={d ?? null} aoFechar={() => setEditando(false)} />
      <Confirmar aberto={excluindo} aoFechar={() => setExcluindo(false)} aoConfirmar={() => excluir.mutate()} carregando={excluir.isPending} perigoso titulo={t('cotacao.excluirTitulo')} texto={t('cotacao.excluirTexto')} />
    </>
  );
}
